import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PDVOperator } from "@/hooks/usePDVOperators";
import logoAltus from "@/assets/logo-altus.png";

interface PDVOperatorLoginProps {
  operators: PDVOperator[];
  onLogin: (operatorName: string) => void;
  restaurantName: string;
}

const PIN_LENGTH = 6;

export function PDVOperatorLogin({ operators, onLogin, restaurantName }: PDVOperatorLoginProps) {
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [error, setError] = useState(false);

  const handleDigit = useCallback((digit: number) => {
    setError(false);
    setPin((prev) => (prev.length < PIN_LENGTH ? prev + digit : prev));
  }, []);
  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1));
    setError(false);
  }, []);

  const handleClear = useCallback(() => {
    setPin("");
    setError(false);
  }, []);

  const handleConfirm = useCallback(() => {
    if (pin.length === 0) return;
    const matched = operators.find((op) => op.pin === pin);
    if (matched) {
      onLogin(matched.name);
    } else {
      setShake(true);
      setError(true);
      setTimeout(() => {
        setShake(false);
        setPin("");
      }, 500);
    }
  }, [pin, operators, onLogin]);

  // Keyboard support
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") handleDigit(parseInt(e.key));
      else if (e.key === "Backspace") handleBackspace();
      else if (e.key === "Enter") handleConfirm();
      else if (e.key === "Escape") handleClear();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleDigit, handleBackspace, handleConfirm, handleClear]);

  const numpadKeys = [
    { label: "7", action: () => handleDigit(7) },
    { label: "8", action: () => handleDigit(8) },
    { label: "9", action: () => handleDigit(9) },
    { label: "⌫", action: handleBackspace, variant: "backspace" as const },
    { label: "4", action: () => handleDigit(4) },
    { label: "5", action: () => handleDigit(5) },
    { label: "6", action: () => handleDigit(6) },
    { label: "✕", action: handleClear, variant: "cancel" as const },
    { label: "1", action: () => handleDigit(1) },
    { label: "2", action: () => handleDigit(2) },
    { label: "3", action: () => handleDigit(3) },
    { label: "✓", action: handleConfirm, variant: "confirm" as const },
    { label: "0", action: () => handleDigit(0), span: 3 },
  ];

  const getKeyClass = (variant?: string) => {
    const base = "flex items-center justify-center rounded-[10px] border text-lg font-bold transition-all duration-120 active:scale-95 select-none touch-manipulation cursor-pointer min-h-[52px]";
    switch (variant) {
      case "cancel":
        return `${base} bg-[#3a0a0a] border-[#7a1a1a] text-[#ff6b6b] text-xl hover:bg-[#4a1010] hover:border-[#cc2222]`;
      case "confirm":
        return `${base} bg-[#0a3d1e] border-[#00c853] text-[#00e676] text-xl hover:bg-[#0d4d26] hover:border-[#00e676]`;
      case "backspace":
        return `${base} bg-[#111829] border-[#1e2d52] text-[#7aabdd] text-base hover:bg-[#142040] hover:border-[#1a5fa0]`;
      default:
        return `${base} bg-[#111829] border-[#1e2d52] text-[#c8dff5] hover:bg-[#142040] hover:border-[#1a5fa0] hover:text-[#4dd0ff] hover:scale-105`;
    }
  };

  return (
    <div className="flex h-full w-full font-['Nunito',sans-serif]">
      {/* Left Panel — Brand */}
      <motion.div
        className="hidden md:flex w-[60%] flex-col items-center justify-center relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #061428 0%, #0a1e3a 50%, #071020 100%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(0,170,255,0.03) 0%, transparent 70%)",
          }}
        />

        <motion.div
          className="flex flex-col items-center z-10"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <img src={logoAltus} alt="Altus Food" className="h-72 w-auto mb-8" />
          <p className="text-4xl text-[#2a5a8c] tracking-[3px] mt-6">
            Sistema de Gestão para Restaurantes
          </p>
        </motion.div>
      </motion.div>

      {/* Right Panel — PIN */}
      <motion.div
        className="flex-1 md:w-[40%] bg-[#0b1020] border-l border-[#1e2d52] flex flex-col items-center justify-center p-6 md:p-8"
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="w-full max-w-[340px] flex flex-col gap-5">
          {/* Title */}
          <h2 className="text-sm font-bold text-[#7aabdd] tracking-[1px] text-center">
            Digite seu PIN
          </h2>

          {/* PIN Display */}
          <motion.div
            className={`flex items-center justify-center gap-3 rounded-[10px] border min-h-[52px] px-4 transition-colors ${
              error ? "border-[#ff4444] bg-[#1a0808]" : "border-[#1e2d52] bg-[#060c18]"
            }`}
            animate={shake ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : {}}
            transition={{ duration: 0.4 }}
          >
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <AnimatePresence key={i} mode="wait">
                {i < pin.length ? (
                  <motion.div
                    key={`filled-${i}`}
                    className="w-3.5 h-3.5 rounded-full bg-[#00aaff]"
                    style={{ boxShadow: "0 0 6px #00aaff66" }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  />
                ) : (
                  <div
                    key={`empty-${i}`}
                    className="w-3.5 h-3.5 rounded-full border-2 border-[#1e2d52]"
                  />
                )}
              </AnimatePresence>
            ))}
          </motion.div>

          {/* Numpad */}
          <div className="grid grid-cols-4 gap-2">
            {numpadKeys.map((key, i) => (
              <motion.button
                key={i}
                type="button"
                className={`${getKeyClass(key.variant)} ${key.span ? `col-span-${key.span}` : ""}`}
                style={key.span ? { gridColumn: `span ${key.span}` } : undefined}
                onClick={key.action}
                disabled={false}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.03 }}
                whileTap={{ scale: 0.95 }}
              >
                {key.label}
              </motion.button>
            ))}
          </div>

          {/* Hint */}
          <p className="text-[11px] text-[#2a4a6c] text-center">
            Digite seu PIN para identificação
          </p>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#080d18] border-t border-[#1e2d52] px-5 py-2 flex justify-between items-center">
        <span className="text-[10px]" style={{ fontFamily: "'Exo 2', sans-serif" }}>
          <span className="text-[#1a3050]">Powered by </span>
          <span className="text-[#00aaff]">ALTUS Food</span>
          <span className="text-[#1a3050]"> · Sistema de Gestão</span>
        </span>
        <span className="text-[10px] text-[#1a3050]">
          {restaurantName}
        </span>
      </div>
    </div>
  );
}
